/**
 * Open a print-friendly window for a ticket.
 *
 * Supports browser-standard A4 print AND thermal 58mm / 80mm formats
 * via CSS @page sizes and a `?w=58` / `?w=80` query parameter.
 */
import { LOGO_URL } from "@/lib/api";

const escape = (s) =>
  String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

function buildPrintHtml(ticket, agencyName, opts = {}) {
  const widthMm = Number(opts.widthMm) || 80;         // 58 | 80 | 0 (= A4)
  const issuedAt = ticket.created_at
    ? new Date(ticket.created_at).toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

  // For thermal we use a small fixed width; for A4 we centre with a max-width
  const pageRule = widthMm
    ? `@page { size: ${widthMm}mm auto; margin: 0; }`
    : `@page { size: A4; margin: 18mm; }`;

  const wrapperStyle = widthMm
    ? `width: ${widthMm}mm; padding: 6mm 4mm; font-family: 'Courier New', monospace; font-size: 11px;`
    : `max-width: 420px; margin: 32px auto; padding: 24px; font-family: -apple-system, system-ui, sans-serif; border: 1px dashed #94A3B8; border-radius: 12px;`;

  const codeFont = widthMm ? "36pt" : "62pt";

  return `<!doctype html>
<html lang="fr">
<head>
  <meta charset="utf-8" />
  <title>Ticket ${escape(ticket.full_ticket_code)}</title>
  <style>
    ${pageRule}
    * { box-sizing: border-box; }
    body { margin: 0; background: white; color: #0F172A; }
    .ticket { ${wrapperStyle} text-align: center; }
    .logo { width: ${widthMm ? "20mm" : "60px"}; height: auto; margin: 0 auto 6px; display: block; }
    .org { font-weight: 700; font-size: ${widthMm ? "12px" : "14px"}; }
    .agency { font-size: ${widthMm ? "10px" : "12px"}; color: #475569; margin-bottom: 8px; }
    .divider { border-top: 1px dashed #94A3B8; margin: 6px 0; }
    .label { font-size: ${widthMm ? "9px" : "11px"}; letter-spacing: 0.18em; text-transform: uppercase; color: #64748B; }
    .code { font-family: 'Courier New', monospace; font-weight: 900; font-size: ${codeFont}; line-height: 1; color: #DC2626; margin: 4px 0 8px; }
    .category { display: inline-block; padding: 3px 10px; border: 1px solid #CBD5E1; border-radius: 4px; font-size: ${widthMm ? "10px" : "12px"}; font-weight: 700; margin: 4px 0; }
    .meta { font-size: ${widthMm ? "10px" : "12px"}; margin: 4px 0; }
    .qr { margin: 8px auto 4px; width: ${widthMm ? "28mm" : "120px"}; }
    .qr img { width: 100%; height: auto; }
    .qr-hint { font-size: ${widthMm ? "8px" : "10px"}; color: #64748B; margin-top: 2px; }
    .msg { margin-top: 8px; font-size: ${widthMm ? "10px" : "12px"}; font-style: italic; color: #475569; }
    .footer { margin-top: 6px; font-size: ${widthMm ? "8px" : "10px"}; color: #94A3B8; letter-spacing: 0.12em; text-transform: uppercase; }
    @media print { body { background: white; } .ticket { border: none !important; margin: 0 !important; } }
  </style>
</head>
<body>
  <div class="ticket">
    <img class="logo" src="${escape(LOGO_URL)}" alt="MAMA" />
    <div class="org">Mutuelle d'Assurance Malagasy</div>
    <div class="agency">${escape(agencyName || ticket.agency_code || "")}</div>
    <div class="divider"></div>
    <div class="label">Numéro de ticket</div>
    <div class="code">${escape(ticket.full_ticket_code)}</div>
    <div class="category">${escape(ticket.category_label || ticket.category_code)}</div>
    <div class="meta">${escape(issuedAt)}</div>
    <div class="divider"></div>
    ${ticket.qr_image ? `
      <div class="qr"><img src="${ticket.qr_image}" alt="QR" /></div>
      <div class="qr-hint">Scannez pour suivre votre ticket</div>
    ` : ""}
    <div class="msg">Merci de patienter, votre numéro sera appelé.</div>
    <div class="footer">MAMA · Sécurité · Engagement · Solidarité</div>
  </div>
  <script>
    window.addEventListener('load', () => {
      setTimeout(() => { window.focus(); window.print(); }, 250);
    });
  </script>
</body>
</html>`;
}

/**
 * Open a new window with the printable ticket layout.
 *
 * @param {object} ticket   The ticket payload from POST /tickets (must include qr_image)
 * @param {string} agencyName  Display name for the agency
 * @param {object} opts     { widthMm?: 58|80|0 }  0 = A4 (browser standard)
 */
export function openTicketPrint(ticket, agencyName, opts = {}) {
  const html = buildPrintHtml(ticket, agencyName, opts);
  const win = window.open("", "_blank", "width=480,height=720");
  if (!win) {
    // Pop-up blocked — fall back to data URL navigation
    const blob = new Blob([html], { type: "text/html" });
    window.open(URL.createObjectURL(blob), "_blank");
    return;
  }
  win.document.write(html);
  win.document.close();
}
